import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import useSEO from '../hooks/useSEO';

const { FiMapPin, FiPhone, FiClock, FiTruck, FiShield, FiCheckCircle, FiHelpCircle } = FiIcons;

const CityPages = ({ city }) => {
  // Apply Dynamic SEO
  useSEO({
    title: `CARB Compliance & Emissions Testing in ${city}, CA | Ramirez Mobile`,
    description: `Expert mobile CARB compliance, Clean Truck Checks, and diesel emissions testing in ${city}, CA. Ramirez Mobile Clean Truck comes to you. Call 909-284-2738.`,
    canonicalRoute: `/${city.toLowerCase().replace(' ', '-')}-ca`
  });

  const localFaq = [
    {
      q: `Do you provide mobile CARB testing in ${city}, CA?`,
      a: `Yes, Ramirez Mobile Clean Truck provides 100% mobile CARB Clean Truck Checks directly at your location in ${city}. We bring the diagnostic equipment to your yard to eliminate downtime.`
    },
    {
      q: `How much does a Clean Truck Check cost in ${city}?`,
      a: `Our mobile Clean Truck Check services in ${city} start at $150 per truck. We offer special fleet pricing if you have multiple diesel vehicles located at the same yard.`
    },
    {
      q: `How quickly can you arrive in ${city} for an emergency test?`,
      a: `We offer same-day and emergency dispatch services throughout ${city} and surrounding areas. Call us at (909) 284-2738 for immediate assistance.`
    }
  ];

  // Inject LocalBusiness & FAQ Schema scoped to the dynamic City
  useEffect(() => {
    const scriptId = `city-schema-json-ld-${city.replace(/\s+/g, '')}`;
    if (!document.getElementById(scriptId)) {
      const script = document.createElement('script');
      script.id = scriptId;
      script.type = 'application/ld+json';
      script.text = JSON.stringify({
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "AutoRepair",
            "name": `Ramirez Mobile Clean Truck - ${city} Service Area`,
            "description": `Professional mobile CARB compliance, HD I/M testing, and emissions services for diesel fleets in ${city}, California.`,
            "url": `https://ramirezmobilecleantruck.com/${city.toLowerCase().replace(' ', '-')}-ca`,
            "telephone": "+19092842738",
            "areaServed": {
              "@type": "City",
              "name": city,
              "addressRegion": "CA"
            }
          },
          {
            "@type": "FAQPage",
            "mainEntity": localFaq.map(faq => ({
              "@type": "Question",
              "name": faq.q,
              "acceptedAnswer": {
                "@type": "Answer",
                "text": faq.a
              }
            }))
          }
        ]
      });
      document.head.appendChild(script);
    }
    return () => {
      const script = document.getElementById(scriptId);
      if (script) document.head.removeChild(script);
    };
  }, [city]);

  const cityData = {
    'Ontario': {
      description: 'Ontario, California is a central hub for logistics and transportation, making CARB compliance critical for local fleets. Ramirez Mobile Clean Truck provides expert mobile Clean Truck Checks, opacity testing, and emissions compliance services directly to your facility in Ontario. Avoid downtime with our fast, professional service.',
      landmarks: ['Ontario International Airport', 'Victoria Gardens', 'Citizens Business Bank Arena'],
      zipCodes: ['91761', '91762', '91764'],
      population: '175,000+',
      majorEmployers: ['Amazon', 'UPS', 'FedEx']
    },
    'Rancho Cucamonga': {
      description: 'Serving Rancho Cucamonga trucking companies with reliable mobile CARB compliance. The technicians at Ramirez Mobile Clean Truck specialize in diesel emissions testing and fleet maintenance, ensuring your vehicles meet all California state regulations without disrupting your operations.',
      landmarks: ['Victoria Gardens', 'LoanMart Field', 'Cucamonga-Guasti Regional Park'],
      zipCodes: ['91701', '91729', '91730', '91737', '91739'],
      population: '177,000+',
      majorEmployers: ['Coca-Cola', 'Walmart Distribution', 'Sysco Foods']
    },
    'Upland': {
      description: 'Upland fleet operators trust Ramirez Mobile Clean Truck for accurate and timely CARB compliance services. From opacity testing to complete fleet audits, we provide the expertise needed to keep your trucks legal and on the road.',
      landmarks: ['Upland Memorial Park', 'Cooper Regional History Museum', 'San Antonio Heights'],
      zipCodes: ['91784', '91785', '91786'],
      population: '79,000+',
      majorEmployers: ['San Antonio Regional Hospital', 'Upland Unified School District']
    },
    'Fontana': {
      description: 'Fontana is a trucking powerhouse, and Ramirez Mobile Clean Truck is here to support it. We offer comprehensive mobile CARB compliance testing, diagnostics, and force regens for fleets of all sizes in Fontana and the surrounding industrial areas.',
      landmarks: ['Auto Club Speedway', 'Fontana Park', 'Sierra Lakes Golf Club'],
      zipCodes: ['92335', '92336', '92337'],
      population: '214,000+',
      majorEmployers: ['Amazon', 'Kaiser Permanente', 'Fontana Unified School District']
    },
    'Chino': {
      description: 'Keep your Chino-based fleet compliant with Ramirez Mobile Clean Truck\'s professional mobile services. We specialize in heavy-duty diesel truck inspections and CARB testing, bringing our expertise directly to your yard.',
      landmarks: ['Prado Regional Park', 'Chino Hills State Park', 'Yanks Air Museum'],
      zipCodes: ['91708', '91710'],
      population: '91,000+',
      majorEmployers: ['Boys Republic', 'California Institution for Men', 'Chino Valley Medical Center']
    },
    'Pomona': {
      description: 'Pomona trucking businesses rely on Ramirez Mobile Clean Truck for efficient and accurate CARB compliance testing. We handle everything from single truck inspections to large fleet management, ensuring you pass Clean Truck Checks with ease.',
      landmarks: ['Fairplex', 'Cal Poly Pomona', 'Pomona Fox Theater'],
      zipCodes: ['91766', '91767', '91768', '91769'],
      population: '153,000+',
      majorEmployers: ['Cal Poly Pomona', 'Pomona Valley Hospital Medical Center', 'LA County Fair']
    },
    'San Bernardino': {
      description: 'As a major logistics center, San Bernardino requires strict adherence to emissions laws. Ramirez Mobile Clean Truck provides rigorous CARB compliance testing and diagnostics to help you avoid fines and keep your freight moving.',
      landmarks: ['San Bernardino National Forest', 'California State University', 'Glen Helen Regional Park'],
      zipCodes: ['92401', '92404', '92405', '92407', '92408', '92410', '92411'],
      population: '222,000+',
      majorEmployers: ['Amazon', 'Stater Bros. Markets', 'County of San Bernardino']
    },
    'Riverside': {
      description: 'We serve Riverside with top-tier mobile CARB compliance solutions. Whether you need an annual inspection or emergency diagnostics, Ramirez Mobile Clean Truck’s experienced technicians are ready to assist you on-site.',
      landmarks: ['Mission Inn Hotel & Spa', 'UC Riverside', 'Mount Rubidoux Park'],
      zipCodes: ['92501', '92503', '92504', '92505', '92506', '92507', '92508'],
      population: '331,000+',
      majorEmployers: ['University of California Riverside', 'Riverside County', 'Kaiser Permanente']
    }
  };

  const data = cityData[city] || cityData['Ontario'];

  const services = [
    'Mobile Emissions Testing',
    'CARB Compliance Testing',
    'Smoke Opacity Testing',
    'Fleet Management',
    'Emergency Diagnostics',
    'DPF Force Regens'
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="relative bg-blue-900 text-white py-24 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1471039497385-b6d6ba609f9c?auto=format&fit=crop&w=1920&q=80" 
            alt={`California Highway in ${city}`} 
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-blue-900/90 to-blue-800/80"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <motion.h1 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-5xl font-bold mb-6"
            >
              Ramirez Mobile Clean Truck: CARB Compliance Services in {city}, California
            </motion.h1>
            <motion.h2 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-2xl mb-8"
            >
              Professional Mobile Diesel Emissions Testing
            </motion.h2>
            <motion.p 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="text-xl text-blue-100 mb-8"
            >
              Ramirez Mobile Clean Truck provides comprehensive CARB compliance services directly to your yard throughout {city} and surrounding areas.
            </motion.p>
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="flex flex-col sm:flex-row gap-4 justify-center"
            >
              <a 
                href="tel:9092842738" 
                className="bg-yellow-500 text-blue-900 font-bold px-8 py-4 rounded-lg hover:bg-yellow-400 transition-colors text-lg flex items-center justify-center space-x-2 shadow-lg"
              >
                <SafeIcon icon={FiPhone} />
                <span>Call (909) 284-2738</span>
              </a>
              <Link 
                to="/contact" 
                className="border-2 border-white text-white px-8 py-4 rounded-lg hover:bg-white hover:text-blue-900 transition-colors text-lg shadow-lg"
              >
                Get Free Quote
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* City Info */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-800 mb-6">
                CARB Compliance in {city}, CA
              </h2>
              <h3 className="text-2xl text-gray-600 mb-6">
                Local Expertise for {city} Trucking Companies
              </h3>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">{data.description}</p>
              
              <div className="grid grid-cols-2 gap-6 mb-8">
                <div>
                  <h4 className="font-semibold text-gray-800 mb-2">Population</h4>
                  <p className="text-gray-600">{data.population}</p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-800 mb-2">Zip Codes Served</h4>
                  <p className="text-gray-600 text-sm">{data.zipCodes.join(', ')}</p>
                </div>
              </div>

              <div className="bg-blue-50 p-6 rounded-lg border border-blue-100">
                <h4 className="text-xl font-semibold text-gray-800 mb-4">
                  Why {city} Businesses Choose Ramirez Mobile
                </h4>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <SafeIcon icon={FiCheckCircle} className="text-green-500" />
                    <span className="text-gray-700">Deep knowledge of {city} trucking routes and yards</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <SafeIcon icon={FiCheckCircle} className="text-green-500" />
                    <span className="text-gray-700">100% Mobile service - we come to you</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <SafeIcon icon={FiCheckCircle} className="text-green-500" />
                    <span className="text-gray-700">Same-day emergency service available</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <SafeIcon icon={FiCheckCircle} className="text-green-500" />
                    <span className="text-gray-700">Official CARB compliance specialists</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 p-8 rounded-lg shadow-md border border-gray-100">
              <div className="text-center mb-8">
                <SafeIcon icon={FiMapPin} className="text-4xl text-blue-600 mb-4 mx-auto block" />
                <h3 className="text-2xl font-bold text-gray-800">Serving {city}</h3>
                <p className="text-gray-600">Mobile CARB compliance services</p>
              </div>

              <div className="space-y-6">
                <div>
                  <h4 className="font-semibold text-gray-800 mb-2">Service Hours</h4>
                  <div className="flex items-center space-x-2">
                    <SafeIcon icon={FiClock} className="text-blue-600" />
                    <span className="text-gray-600">Monday - Saturday: 7AM - 6PM</span>
                  </div>
                  <p className="text-sm text-red-600 mt-1 font-semibold">Emergency service available</p>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-800 mb-2">Contact Information</h4>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <SafeIcon icon={FiPhone} className="text-blue-600" />
                      <a href="tel:9092842738" className="text-blue-600 hover:underline font-bold">
                        (909) 284-2738
                      </a>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-800 mb-3">Areas We Cover</h4>
                  <div className="text-sm text-gray-600 space-y-2">
                    {data.landmarks.map((landmark, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 bg-blue-500 rounded-full"></span>
                        Near {landmark}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services in City */}
      <section className="py-20 bg-gray-100">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">
              CARB Compliance Services in {city}
            </h2>
            <h3 className="text-2xl text-gray-600">
              Comprehensive Solutions for {city} Trucking Companies
            </h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white p-8 rounded-lg shadow-md hover:shadow-lg transition-shadow text-center border-t-4 border-blue-600"
              >
                <SafeIcon icon={FiShield} className="text-4xl text-blue-600 mb-4 mx-auto block" />
                <h4 className="text-xl font-bold text-gray-800 mb-4">{service}</h4>
                <p className="text-gray-600 mb-6">
                  Professional {service.toLowerCase()} services performed directly at your facility in {city}, CA.
                </p>
                <a 
                  href="tel:9092842738" 
                  className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors inline-block font-semibold"
                >
                  Call Now
                </a>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Local FAQ Section (Crucial for AI and Local SEO) */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <SafeIcon icon={FiHelpCircle} className="text-4xl text-blue-600 mx-auto mb-4" />
              <h2 className="text-3xl font-bold text-gray-800 mb-4">
                Frequently Asked Questions in {city}
              </h2>
              <p className="text-gray-600">Common questions from local fleet managers.</p>
            </div>
            
            <div className="space-y-6">
              {localFaq.map((faq, index) => (
                <div key={index} className="bg-gray-50 border border-gray-200 p-6 rounded-lg">
                  <h4 className="text-xl font-bold text-gray-800 mb-3">{faq.q}</h4>
                  <p className="text-gray-600 leading-relaxed">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Local Industry */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold mb-4">
                {city}'s Trucking & Logistics Industry
              </h2>
              <h3 className="text-2xl text-gray-400">
                Supporting Local Transportation Companies
              </h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
              <div className="bg-gray-800 p-8 rounded-xl border border-gray-700">
                <h4 className="text-2xl font-bold mb-6 text-blue-400">
                  Major Hubs in {city}
                </h4>
                <div className="space-y-4">
                  {data.majorEmployers.map((employer, index) => (
                    <div key={index} className="flex items-center space-x-3">
                      <div className="p-2 bg-blue-900/50 rounded-full">
                        <SafeIcon icon={FiTruck} className="text-blue-400" />
                      </div>
                      <span className="text-gray-200 font-medium">{employer}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="bg-gray-800 p-8 rounded-xl border border-gray-700">
                <h4 className="text-2xl font-bold mb-6 text-yellow-400">
                  Why CARB Compliance Matters Here
                </h4>
                <div className="space-y-4 text-gray-300 leading-relaxed">
                  <p>
                    California's strict emissions standards require regular CARB compliance testing and maintenance for all diesel trucks operating in {city}.
                  </p>
                  <p>
                    Non-compliance can result in significant DMV holds, vehicle impoundment, and business disruption. Ramirez Mobile Clean Truck ensures your fleet stays compliant and operational without leaving your yard.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Local CTA */}
      <section className="relative py-20 bg-blue-900 text-white overflow-hidden">
        <div className="absolute inset-0 z-0 opacity-20">
          <img 
            src="https://images.unsplash.com/photo-1519003722824-194d4455a60c?auto=format&fit=crop&w=1920&q=80" 
            alt="Highway traffic" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container mx-auto px-4 text-center relative z-10">
          <h2 className="text-4xl font-bold mb-4">
            Need CARB Compliance Service in {city}?
          </h2>
          <h3 className="text-2xl mb-8 text-blue-200">
            Mobile Service Available Throughout {city}, California
          </h3>
          <p className="text-xl mb-8 text-white max-w-3xl mx-auto">
            Our expert technicians provide comprehensive CARB compliance services throughout {city} and surrounding areas. Call Ramirez Mobile Clean Truck now for same-day service!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="tel:9092842738" 
              className="bg-yellow-500 text-blue-900 font-bold px-8 py-4 rounded-lg hover:bg-yellow-400 transition-colors text-lg"
            >
              Call (909) 284-2738
            </a>
            <Link 
              to="/contact" 
              className="border-2 border-white text-white px-8 py-4 rounded-lg hover:bg-white hover:text-blue-900 transition-colors text-lg"
            >
              Schedule Service
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CityPages;